<template>
  <VDialogV3
    position="right"
    width="600px"
    height="100vh"
    :loading="localContract === null"
    :context-menu="ContractsExternalService.contextMenu"
    :entity="localContract"
    :tabs="tabs"
    v-model:active-tab="activeTab"
    :submit-disabled="submitDisabled"
    @submit="onSubmit"
  >
    <template #header>
      <div class="title">{{ title }}</div>
      <VBadges>
        <VBadge
          v-if="localContract?.isDeleted"
          class="warning"
        >
          Удалён
        </VBadge>
      </VBadges>
    </template>

    <FilesList
      v-if="editMode && activeTab.id === 'files'"
      :entities="fileEntities"
    />

    <template v-else>
      <section>
        <VFormItem
          label="Номер"
          layout="vertical"
          with-asterisk
        >
          <VInput
            v-model="localContract.number"
            required
            :disabled="editMode"
          />

          <span>от</span>

          <VDateInput
            v-model="localContract.validityPeriod.start"
            required
            :disabled="editMode"
          />
        </VFormItem>

        <VSelect
          v-model="localContract.deliveryType"
          label="Тип доставки"
          layout="vertical"
          :clearable="false"
          :options="deliveryTypeList"
          required
        />

        <VSwitch
          v-model="localContract.isDeliveryPaidByBuyer"
          theme="slider"
          checked-label="Покупатель оплачивает доставку"
          unchecked-label="Продавец доставляет за свой счёт"
        />
      </section>

      <hr>

      <section class="partner">
        <header>Продавец</header>

        <VSelect
          v-model="localContract.seller"
          label="Партнер"
          layout="vertical"
          disabled
          option-identity="id"
          option-label="name"
          :options="sellerPartnerOptions"
        />

        <VSelect
          v-model="localContract.sellerCompany"
          label="Компания"
          layout="vertical"
          :clearable="false"
          :disabled="editMode"
          option-description="description"
          :options="sellerCompaniesOptions"
          required
        />
      </section>

      <section class="partner">
        <header>Покупатель</header>

        <VSelect
          v-model="localContract.buyer"
          label="Партнер"
          layout="vertical"
          disabled
          option-identity="id"
          option-label="name"
          :options="buyerPartnerOptions"
        />

        <VSelect
          v-model="localContract.buyerCompany"
          label="Компания"
          layout="vertical"
          :disabled="editMode"
          :clearable="false"
          option-description="description"
          :options="buyerCompaniesOptions"
          required
        />
      </section>

      <hr>

      <section class="prices">
        <header>Цены</header>

        <VSwitch
          v-if="isVatPresented"
          v-model="localContract.isVatIncluded"
          :disabled="!isVatPresented"
          theme="slider"
          layout="vertical"
          checked-label="НДС включительно"
          unchecked-label="НДС сверху"
        />

        <VInput
          v-else
          layout="vertical"
          disabled
          value="НДС не облагается"
        />
      </section>

      <hr>

      <PaymentTermEditor
        :entity="localContract"
        title="Оплата"
      />
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import ContractExternal 				from "@/domains/Contracts/ContractExternal";
import ContractsExternalService from "@/domains/Contracts/ContractsExternalService";
import EntityPointer 						from "@/domains/EntityPointer/EntityPointer";

import PaymentTermEditor 				from "@/domains/PaymentTerms/components/PaymentTermEditor/PaymentTermEditor.vue";
import FilesList 								from "@/core/services/Files/components/FilesList/FilesList.vue";
import DeliveryTypesService from "@/domains/DeliveryTypes/DeliveryTypesService";

export default defineComponent({
  name: 'ContractExternalCard',

  components: {
    PaymentTermEditor,
    FilesList,
  },

  props: {
    contract: {
      type:    ContractExternal,
      default: null,
    },
  },

  data() {
    return {
      ContractsExternalService,

      localContract: null as ContractExternal | null,
      activeTab: {
        id: 'main',
        name: 'Основное'
      },
    };
  },

  mounted() {
    this.init();
  },

  computed: {
    editMode(): boolean {
      return Boolean(this.localContract?.id);
    },

    title(): string {
      if (!this.localContract) return '';
      if (!this.editMode) return 'Создание договора';

      return `Договор №${this.localContract.number} от ${this.localContract.validityPeriod.start.toLocalString()}`;
    },

    submitDisabled(): string | null {
      if (this.editMode && this.$core.utils.isEqual(this.contract, this.localContract)) {
        return 'Нет изменений';
      }
      return null;
    },

    sellerPartnerOptions(): any[] {
      return this.localContract ? [this.localContract.seller] : [];
    },

    sellerCompaniesOptions(): any[] {
      if (!this.localContract) return [];

      return this.localContract.seller.companies.map((company: any) => ({
        id:          company.id,
        name:        company.name,
        isVatPayer:  company.isVatPayer,
        description: this.$core.utils.isObject(company.additionalAttributes) && this.$core.utils.isObject(company.additionalAttributes.inn)
          ? `ИНН: ${company.additionalAttributes.inn.value}`
          : 'Не указан',
      }));
    },

    buyerPartnerOptions(): any[] {
      return this.localContract ? [this.localContract.buyer] : [];
    },

    buyerCompaniesOptions(): any[] {
      if (!this.localContract) return [];

      return this.localContract.buyer.companies.map((company: any) => ({
        id:          company.id,
        name:        company.name,
        description: this.$core.utils.isObject(company.additionalAttributes) && this.$core.utils.isObject(company.additionalAttributes.inn)
          ? `ИНН: ${company.additionalAttributes.inn.value}`
          : 'Не указан',
      }));
    },

    isVatPresented(): boolean {
      return Boolean(this.localContract?.sellerCompany?.isVatPayer);
    },

    deliveryTypeList(): any[] {
      return DeliveryTypesService.deliveryTypes;
    },

    tabs(): { id: string; name: string }[] {
      return this.$core.currentPartner.isBuyer && this.editMode
        ? [{ id: 'main', name: 'Основное' }, { id: 'files', name: 'Файлы' }]
        : [{ id: 'main', name: 'Основное' }];
    },

    fileEntities(): EntityPointer[] {
      return [new EntityPointer('contractExternal', this.contract.id, this.title)];
    },
  },

  beforeUnmount() {
    this.$emit('component:before-unmount');
  },

  methods: {
    /**
     * Готовит локальную копию договора для редактирования.
     */

    init(): void {
      this.localContract = this.$core.utils.cloneDeep(this.contract);
    },

    /**
     * Сохранение договора: создание или обновление, затем закрытие карточки.
     * Валидацию формы делает сам VDialogV3 — сюда @submit приходит уже валидным.
     */

    async onSubmit(): Promise<void> {
      if (!this.localContract) return;

      if (this.editMode) {
        const success = await ContractsExternalService.update(this.localContract);
        if (success) {
          this.$emit(ContractExternal.Events.Updated, this.localContract);
          this.$core.dialogs.ContractExternalCard.hide();
        }
      } else {
        const success = await ContractsExternalService.add(this.localContract);
        if (success) {
          this.$emit(ContractExternal.Events.Created, this.localContract);
          this.$core.dialogs.ContractExternalCard.hide();
        }
      }
    },
  },
});
</script>
